// زرّا الحضور والانصراف في الترويسة — **التسجيل بنقرة واحدة من أي شاشة**.
//
// المشكلة التي يحلّها: وحدة الحضور موجودة وتعمل، لكنها خلف تبويب — فالموظف لا يسجّل إلا بعد
// أن يتذكّر التبويب ويفتحه. فصار الزرّان أمامه دائمًا.
//
// **والشريط للنقرة وحدها** (قرار المالك): لا سطر حالة دائم ولا زرّ «بالتفصيل» — حالة اليوم
// كاملةً مكانها تبويب «الحضور والانصراف». ويبقى **تأكيدٌ واحد بعد التسجيل**: الحدث لا يُحذف،
// فلو لم يعرف الموظف أن نقرته وصلت لنقر ثانيةً وترك في سجلّه بصمةً تُصحَّح ولا تُمحى — ولذلك
// يُقال له «تم تسجيل الحضور الساعة …» بالوقت الذي أعاده الخادم، لا بوقت جهازه.
//
// ولا نسخة ثانية من وحدة الحضور: نفس منطق النقرة في `useTap` ونفس مساراتها وقواعدها — الوقت
// من الخادم، والموقع اختياري ورفضه لا يمنع التسجيل، ولا بيانات جهاز، ولا مراقبة مستمرة.
//
// ولا تظهر لغير المسجَّلين في الوحدة إطلاقًا: الأب يمرّر `enrolled`، والخادم يرفض بـ403 على
// كل حال — فالإخفاء ترتيبٌ للواجهة لا حماية.
import { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { get, type AttendanceKind, type AttendanceToday } from '../lib/api.ts';
import { playNotificationSound } from '../notifications/notificationSound.ts';
import { markNudgeShown, nudgeFor, nudgeShown, type Nudge } from './attendanceNudge.ts';
import { isOpenSession } from './TapButtons.tsx';
import { cairoTimeAr } from '../lib/format.ts';
import { TapConfirm } from './TapConfirm.tsx';
import { useTap } from './useTap.ts';

const KIND_AR: Record<AttendanceKind, string> = { CHECK_IN: 'الحضور', CHECK_OUT: 'الانصراف' };

export function AttendanceQuickTap() {
  /**
   * **وصار يقرأ حالة اليوم** (قرار المالك 23/09/2026): ليُذكّر بالحضور أوّل اليوم وبالانصراف قبل
   * موعده بعشر دقائق، وليُبرز الزرّ الصحيح (الانصراف ما دامت الجلسة مفتوحة). قراءةٌ كل دقيقة —
   * خفيفة، ولا تُعرض حالةٌ إلا التذكير.
   */
  const [today, setToday] = useState<AttendanceToday | null>(null);
  const [nudge, setNudge] = useState<Nudge | null>(null);
  const load = useCallback(async () => {
    try { setToday(await get<AttendanceToday>('/attendance/today')); } catch { /* التذكير رفاهية */ }
  }, []);
  useEffect(() => {
    void load();
    const t = window.setInterval(() => { void load(); }, 60_000);
    return () => window.clearInterval(t);
  }, [load]);
  useEffect(() => {
    const n = nudgeFor(today);
    if (!n || !today || nudgeShown(today.anchorDate, n.kind)) return;
    markNudgeShown(today.anchorDate, n.kind);
    setNudge(n);
    playNotificationSound();
  }, [today]);
  const { busy, error, done, tap } = useTap(async () => { setNudge(null); await load(); });
  const expected: AttendanceKind = isOpenSession(today) ? 'CHECK_OUT' : 'CHECK_IN';
  const cls = (k: AttendanceKind) => `${k === expected ? 'btn-primary' : 'btn-ghost'} px-3 py-1 text-sm`;
  // نفس تأكيد شاشة الحضور حرفيًا: البصمة واحدة من أي مكان ضُغط الزرّ، فالتحذير واحد
  const [confirm, setConfirm] = useState<AttendanceKind | null>(null);

  return (
    <section aria-label="تسجيل الحضور والانصراف"
      className="flex flex-wrap items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-1.5">
      <button type="button" className={cls('CHECK_IN')}
        disabled={busy === 'CHECK_IN'} onClick={() => setConfirm('CHECK_IN')}>
        {busy === 'CHECK_IN' ? 'جارٍ التسجيل…' : 'تسجيل الحضور'}
      </button>
      <button type="button" className={cls('CHECK_OUT')}
        disabled={busy === 'CHECK_OUT'} onClick={() => setConfirm('CHECK_OUT')}>
        {busy === 'CHECK_OUT' ? 'جارٍ التسجيل…' : 'تسجيل الانصراف'}
      </button>

      {/* تأكيد النقرة التي تمّت الآن — لا حالة اليوم: يمنع النقرة الثانية التي لا تُمحى */}
      {done && !error && (
        <span role="status" className="text-sm font-medium text-emerald-800">
          تم تسجيل {KIND_AR[done.kind]} الساعة {cairoTimeAr(done.at)}
        </span>
      )}
      {done?.lateNotice && !error && (
        <span role="alert" className="text-sm font-semibold text-rose-700">⏰ {done.lateNotice}</span>
      )}

      {error && <span role="alert" className="w-full text-xs text-rose-700">{error}</span>}

      {/* التذكير: يظهر مرّةً في اليوم لكل نوع، بصوت، ويُغلق بضغطة أو بالبصمة نفسها */}
      {/* **في body لا داخل الشريط** (المراجعة الشاملة 02/10/2026): داخل شريط الموبايل كانت محدِّداته تلوّن نصّه وردي باهت
          على أصفر فلا يُقرأ، وداخل صفّ الكمبيوتر المخفي على الموبايل كان يرنّ ولا يظهر. البوابة تُخرجه من الاثنين. */}
      {nudge && createPortal(
        <div role="alert" className="fixed inset-x-3 bottom-24 z-50 mx-auto flex max-w-md items-center gap-3 rounded-2xl
          border border-amber-300 bg-amber-50 px-4 py-3 text-amber-950 shadow-xl lg:bottom-6">
          <span aria-hidden="true" className="text-2xl">⏰</span>
          <span className="flex-1 text-sm font-bold">{nudge.text}</span>
          <button type="button" className="btn-primary px-3 py-1.5 text-sm"
            onClick={() => { setConfirm(nudge.kind); setNudge(null); }}>
            {nudge.kind === 'CHECK_IN' ? 'سجّل الحضور' : 'سجّل الانصراف'}
          </button>
          <button type="button" aria-label="إغلاق التذكير" className="px-1 text-amber-800" onClick={() => setNudge(null)}>✕</button>
        </div>,
        document.body,
      )}

      {confirm && (
        <TapConfirm kind={confirm} busy={busy === confirm} onCancel={() => setConfirm(null)}
          onConfirm={() => void (async () => { await tap(confirm); setConfirm(null); })()} />
      )}
    </section>
  );
}
